import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { InputAdornment } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { format, parse, isDate } from 'date-fns'

import axios from '../../dataClient/dataClient'
import { formatDateInput } from '../../util/strings'
import { constructDateFromDateAndTime } from '../../util/formatting'
import { payInPersonSchema } from '../../schemas/payInPersonSchema'
import { BoldBodyText, BoldFormTitle, SmallBodyText } from '../styles/Text'
import { FlexColumn, FlexEndContainer, MedBGContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers'
import { ErrorMessage, SuccessMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import CalendarDateButton from '../common/CalendarDateButton'
import FormattedInput from '../inputFields/FormattedInput'
import FormInput from '../inputFields/FormInput'
import TimeSelector from '../inputFields/TimeSelector'

const AmountContainer = styled(MedBGContainer)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px;
  gap: 20px;
`

const AmountValue = styled(BoldBodyText)`
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
`

const AmountFootnote = styled(SmallBodyText.withComponent('span'))`
  font-size: 12px;
  margin: 0;
`

function PayInPersonPopUp({ billId, secondaryId, amount, includesTax = true, open = false, onClose = () => {}, onSuccess = () => {} }) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)

  const normalizedAmount = useMemo(() => {
    return amount.toFixed(2)
  }, [amount])

  const methods = useForm({
    resolver: yupResolver(payInPersonSchema),
    defaultValues: {
      paymentDate: format(new Date(), 'MMM-dd-yyyy'),
      paymentTime: new Date(),
      note: ''
    }
  })

  const { handleSubmit, reset, setValue, watch } = methods

  useEffect(() => {
    if (!open) return

    setError(false)
    setIsCompleted(false)

    reset({
      paymentDate: format(new Date(), 'MMM-dd-yyyy'),
      paymentTime: new Date(),
      note: ''
    })
  }, [open, reset])

  const paymentDate = watch('paymentDate')
  const calendarInitialDate = isDate(paymentDate) ? paymentDate : parse(paymentDate, 'MMM-dd-yyyy', new Date())

  const handleConfirmDate = selectedDate => {
    const formatted = format(selectedDate, 'MMM-dd-yyyy')
    setValue('paymentDate', formatted)
  }

  function onSubmit(formValues) {
    setIsLoading(true)
    setError(false)

    const parsedDate = isDate(formValues.paymentDate) ? formValues.paymentDate : parse(formValues.paymentDate, 'MMM-dd-yyyy', new Date())
    const paymentDateTime = constructDateFromDateAndTime(parsedDate, formValues.paymentTime)

    axios
      .post(`/bill/${billId}/pay-in-person`, {
        paymentDate: paymentDateTime,
        paymentNote: formValues.note || null
      })
      .then(() => {
        setIsCompleted(true)
        onSuccess()
      })
      .catch(err => {
        console.error('Failed to update in person payment', err)
        setError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onError = (errors, e) => console.error('PayInPersonPopUp validation error', errors, e)

  return (
    <StyledDialog fullWidth open={open} onClose={onClose} keepMounted={false} aria-labelledby="pay-in-person-pop-up">
      <StyledDialogContent>
        <FormProvider {...methods}>
          <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
            <FlexColumn gap="20px">
              <BoldFormTitle>Confirm In Person Payment: {secondaryId}</BoldFormTitle>
              <AmountContainer>
                <BoldBodyText>Amount</BoldBodyText>
                <AmountValue>
                  ${normalizedAmount} CAD
                  {includesTax && <AmountFootnote>(incl. tax)</AmountFootnote>}
                </AmountValue>
              </AmountContainer>

              {isCompleted ? (
                <FlexColumn gap="20px">
                  <SuccessMessage>In person payment successfully updated.</SuccessMessage>
                </FlexColumn>
              ) : (
                <FlexColumn gap="20px">
                  <FormattedInput
                    id="paymentDate"
                    label="Payment Date"
                    required
                    handleFormatValue={formatDateInput}
                    inputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarDateButton initialValue={calendarInitialDate} title="Payment Date" onConfirm={handleConfirmDate} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TimeSelector id="paymentTime" label="Payment Time" defaultValue={new Date()} required />
                  <FormInput id="note" label="Note" multiline minRows={1} />
                  {error && <ErrorMessage>There was an error confirming the in person payment. Please try again.</ErrorMessage>}
                </FlexColumn>
              )}

              <FlexEndContainer>
                {isCompleted ? (
                  <SecondaryButton type="button" onClick={onClose}>
                    Close
                  </SecondaryButton>
                ) : (
                  <>
                    <SecondaryButton type="button" onClick={onClose} disabled={isLoading}>
                      Cancel
                    </SecondaryButton>
                    <Button type="submit" loading={isLoading}>
                      Confirm
                    </Button>
                  </>
                )}
              </FlexEndContainer>
            </FlexColumn>
          </PaddedForm>
        </FormProvider>
      </StyledDialogContent>
    </StyledDialog>
  )
}

PayInPersonPopUp.propTypes = {
  billId: PropTypes.string.isRequired,
  secondaryId: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  includesTax: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
}

export default PayInPersonPopUp
